import type {
  ChartDataPoint,
  SeriesColorConfig,
} from '@highereducation/hen-ui';

import type { TransformRow } from '../../transforms/types';
import type { ChartPresentation } from '../types';
import { seriesToColorConfigs } from './seriesConfig';

export { seriesToChartConfig, seriesToColorConfigs } from './seriesConfig';
export type { SeriesPresentation } from './seriesConfig';
export {
  CHART_VALUE_FORMATS,
  axisNumberFormat,
  metricNumberFormat,
  isChartValueFormat,
} from './valueFormat';
export type { ChartValueFormat, ValueFormat } from './valueFormat';
export { resolveFrameCopy } from './frameCopy';
export type { FrameCopy } from './frameCopy';
export {
  buildDataTableColumns,
  dataTableHeaderKey,
  resolveCellFormat,
} from './dataTableColumns';
export type { ResolvedTableColumn } from './dataTableColumns';

/**
 * Presentation props that don't depend on which series a chart draws.
 *
 * Unauthored values are emitted as `undefined` rather than omitted, and are
 * never defaulted here: the renderer's own defaults are the documented ones,
 * and a value invented at this layer would silently outrank them.
 */
export function chartPresentationProps(
  presentation: ChartPresentation | undefined,
): {
  seriesColorConfigs: Record<string, SeriesColorConfig> | undefined;
  height: number | undefined;
  fillContainer: boolean | undefined;
} {
  return {
    seriesColorConfigs: seriesToColorConfigs(presentation?.series ?? []),
    height: presentation?.height,
    fillContainer: presentation?.fillContainer,
  };
}

/**
 * Rows as chart data points.
 *
 * A lake row can hold anything; a chart's data type says every cell is a
 * scalar. That holds for the columns a chart reads — they are column-checked
 * and series-coerced before they get here — and is unenforceable for the rest,
 * which the chart never touches. Narrowing in one named place keeps that claim
 * reviewable instead of repeating it as a cast at every render call.
 */
export function asChartData(rows: readonly TransformRow[]): ChartDataPoint[] {
  return rows as ChartDataPoint[];
}
